import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Calculator,
  Download,
  Share2,
  TrendingUp,
  AlertTriangle,
  Info,
  PoundSterling,
} from "lucide-react";

interface Inputs {
  company: string;
  headcount: number;
  seniorICCount: number;
  seniorICSalary: number;
  managerCount: number;
  managerSalary: number;
  directorCount: number;
  directorSalary: number;
  vpCount: number;
  vpSalary: number;
  annualTurnoverPct: number;
  productivityGapPct: number;
  monthsToFullRamp: number;
  rampReductionPct: number;
  avgRevenuePerSeniorAccount: number;
  pctOfSeniorsOwningRevenue: number;
  relayAvgFee: number;
}

const DEFAULTS: Inputs = {
  company: "",
  headcount: 250,
  seniorICCount: 30,
  seniorICSalary: 75000,
  managerCount: 12,
  managerSalary: 95000,
  directorCount: 5,
  directorSalary: 130000,
  vpCount: 3,
  vpSalary: 175000,
  annualTurnoverPct: 15,
  productivityGapPct: 50,
  monthsToFullRamp: 6,
  rampReductionPct: 40,
  avgRevenuePerSeniorAccount: 250000,
  pctOfSeniorsOwningRevenue: 30,
  relayAvgFee: 5500,
};

const FOOTNOTES = [
  ['Productivity gap default 50%', 'A new hire typically operates at ~50% of full productivity in their first months. SHRM 2019 retention report and Gartner HR research 2023.'],
  ['Months to full ramp default 6', 'Median time-to-full-productivity for senior individual contributors. Adjust upward (9–12) for first-line managers and director roles.'],
  ['Ramp reduction default 40%', 'Internal Relay benchmark across 2024–2025 engagements. Equivalent to recovering ~2.4 months of the 6-month ramp.'],
  ['Revenue at risk default 30%', 'Share of senior employees who personally own a customer or revenue relationship. CRO functions trend higher (60%+); engineering trends lower.'],
  ['Relay fee default £5,500', 'Standard tier list price. Use the Pricing page for tier-specific quotes.'],
];

function fmt(n: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
}

function calc(i: Inputs) {
  const seniorTotal = i.seniorICCount + i.managerCount + i.directorCount + i.vpCount;
  const departures = Math.round(seniorTotal * (i.annualTurnoverPct / 100));

  // Cost per role band when a person leaves (productivity dip × ramp duration)
  const monthlySalary = (annual: number) => annual / 12;
  const lossPerExit = (annualSalary: number) =>
    monthlySalary(annualSalary) * i.monthsToFullRamp * (i.productivityGapPct / 100);

  const weightedAvgSalary =
    seniorTotal === 0
      ? 0
      : (i.seniorICCount * i.seniorICSalary +
          i.managerCount * i.managerSalary +
          i.directorCount * i.directorSalary +
          i.vpCount * i.vpSalary) /
        seniorTotal;

  const productivityLossPerExit = lossPerExit(weightedAvgSalary);
  const totalProductivityLoss = productivityLossPerExit * departures;

  const revenueAtRiskPerExit =
    i.avgRevenuePerSeniorAccount * (i.pctOfSeniorsOwningRevenue / 100);
  const totalRevenueAtRisk = revenueAtRiskPerExit * departures * 0.15; // assume 15% of at-risk revenue actually churns without handover

  const totalAnnualCost = totalProductivityLoss + totalRevenueAtRisk;

  const recoveredProductivity = totalProductivityLoss * (i.rampReductionPct / 100);
  const recoveredRevenue = totalRevenueAtRisk * 0.7; // structured handover ~70% effective at retaining at-risk revenue
  const totalSavings = recoveredProductivity + recoveredRevenue;

  const annualSpend = i.relayAvgFee * departures;
  const netBenefit = totalSavings - annualSpend;
  const roi = annualSpend > 0 ? (netBenefit / annualSpend) * 100 : 0;
  const paybackTransitions = totalSavings > 0 ? annualSpend / (totalSavings / Math.max(departures, 1)) : 0;

  return {
    seniorTotal,
    departures,
    weightedAvgSalary,
    productivityLossPerExit,
    totalProductivityLoss,
    revenueAtRiskPerExit,
    totalRevenueAtRisk,
    totalAnnualCost,
    recoveredProductivity,
    recoveredRevenue,
    totalSavings,
    annualSpend,
    netBenefit,
    roi,
    paybackTransitions,
  };
}

function loadFromQuery(): Partial<Inputs> {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out: Partial<Inputs> = {};
  for (const k of Object.keys(DEFAULTS) as (keyof Inputs)[]) {
    const v = p.get(k);
    if (v == null) continue;
    if (k === "company") out.company = v;
    else {
      const n = Number(v);
      if (!Number.isNaN(n)) (out as any)[k] = n;
    }
  }
  return out;
}

export default function CalculatorAdvanced() {
  const [, setLocation] = useLocation();
  const [inputs, setInputs] = useState<Inputs>(() => ({ ...DEFAULTS, ...loadFromQuery() }));
  const result = useMemo(() => calc(inputs), [inputs]);

  // Update URL with current params (debounced via timeout)
  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams();
      for (const k of Object.keys(inputs) as (keyof Inputs)[]) {
        const v = inputs[k];
        if (v === DEFAULTS[k]) continue;
        if (typeof v === "string" && v.length === 0) continue;
        p.set(k, String(v));
      }
      const qs = p.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState(null, "", newUrl);
    }, 400);
    return () => clearTimeout(t);
  }, [inputs]);

  function update<K extends keyof Inputs>(k: K, v: Inputs[K]) {
    setInputs((s) => ({ ...s, [k]: v }));
  }

  function num<K extends keyof Inputs>(k: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const n = Number(e.target.value);
      update(k, (Number.isNaN(n) ? 0 : n) as Inputs[K]);
    };
  }

  function copyShareUrl() {
    if (typeof window === "undefined") return;
    navigator.clipboard.writeText(window.location.href).catch(() => {});
  }

  function exportPdf() {
    if (typeof window !== "undefined") {
      window.print();
    }
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .print-card { break-inside: avoid; }
        }
      `}</style>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ROI calculator (advanced)</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Build a defensible business case for your CFO. All defaults are footnoted. Adjust any number to match your
            organisation, then share the URL or export a PDF.
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={copyShareUrl} className="gap-2">
            <Share2 className="h-4 w-4" /> Copy link
          </Button>
          <Button onClick={exportPdf} className="gap-2">
            <Download className="h-4 w-4" /> Export PDF
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Inputs */}
        <div className="lg:col-span-5 space-y-6 no-print">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-4 w-4 text-primary" /> Company
              </CardTitle>
              <CardDescription>For the report header and the share link.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company" className="text-xs">Company name</Label>
                <Input id="company" value={inputs.company} onChange={(e) => update("company", e.target.value)} placeholder="Acme Ltd" />
              </div>
              <div>
                <Label htmlFor="headcount" className="text-xs">Total headcount</Label>
                <Input id="headcount" type="number" value={inputs.headcount} onChange={num("headcount")} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Senior population by role</CardTitle>
              <CardDescription>Counts and average base salary (£) per band.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { count: "seniorICCount", salary: "seniorICSalary", label: "Senior IC" },
                { count: "managerCount", salary: "managerSalary", label: "Manager" },
                { count: "directorCount", salary: "directorSalary", label: "Director" },
                { count: "vpCount", salary: "vpSalary", label: "VP / C-level" },
              ].map((row) => (
                <div key={row.label} className="grid grid-cols-5 gap-3 items-end">
                  <Label className="col-span-2 text-xs pb-2.5">{row.label}</Label>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={inputs[row.count as keyof Inputs] as number}
                      onChange={num(row.count as keyof Inputs)}
                      placeholder="#"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={inputs[row.salary as keyof Inputs] as number}
                      onChange={num(row.salary as keyof Inputs)}
                      placeholder="£"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Assumptions</CardTitle>
              <CardDescription>Hover the i icon for source.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { k: "annualTurnoverPct" as const, label: "Annual senior turnover", suffix: "%" },
                { k: "productivityGapPct" as const, label: "New hire productivity gap", suffix: "%", info: "Defaults to 50% — see footnote 1." },
                { k: "monthsToFullRamp" as const, label: "Months to full productivity", suffix: "mo", info: "Defaults to 6 — see footnote 2." },
                { k: "rampReductionPct" as const, label: "Ramp reduction with Relay", suffix: "%", info: "Defaults to 40% — see footnote 3." },
                { k: "avgRevenuePerSeniorAccount" as const, label: "Avg revenue / senior account", suffix: "£" },
                { k: "pctOfSeniorsOwningRevenue" as const, label: "Seniors owning revenue", suffix: "%", info: "Defaults to 30% — see footnote 4." },
                { k: "relayAvgFee" as const, label: "Relay avg fee per transition", suffix: "£", info: "See Pricing — footnote 5." },
              ].map((row) => (
                <div key={row.k} className="grid grid-cols-5 gap-3 items-center">
                  <Label className="col-span-3 text-xs flex items-center gap-1.5">
                    {row.label}
                    {row.info && <Info className="h-3 w-3 text-muted-foreground" />}
                  </Label>
                  <div className="col-span-2 flex items-center gap-2">
                    <Input
                      type="number"
                      value={inputs[row.k] as number}
                      onChange={num(row.k)}
                    />
                    <span className="text-xs text-muted-foreground w-4">{row.suffix}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="lg:col-span-7 space-y-6">
          <Card className="border-primary/20 bg-primary/5 print-card">
            <CardContent className="p-8 text-center">
              <div className="text-xs uppercase tracking-wider font-semibold text-primary mb-2">
                Projected annual saving
              </div>
              <div className="text-5xl font-black tracking-tight">{fmt(result.totalSavings)}</div>
              <div className="text-sm text-muted-foreground mt-3 inline-flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                Net benefit after Relay fees: <span className="font-semibold text-foreground">{fmt(result.netBenefit)}</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4 print-card">
            <Card>
              <CardContent className="p-5">
                <div className="text-xs text-muted-foreground">Senior departures / year</div>
                <div className="text-3xl font-bold mt-1">{result.departures}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  From {result.seniorTotal} senior staff at {inputs.annualTurnoverPct}% turnover
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-xs text-muted-foreground">Cost of unmanaged exit</div>
                <div className="text-3xl font-bold mt-1">{fmt(result.productivityLossPerExit + result.revenueAtRiskPerExit * 0.15)}</div>
                <div className="text-xs text-muted-foreground mt-1">Per senior departure, including revenue risk</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-xs text-muted-foreground">Annual spend with Relay</div>
                <div className="text-3xl font-bold mt-1">{fmt(result.annualSpend)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {result.departures} transitions × {fmt(inputs.relayAvgFee)} avg fee
                </div>
              </CardContent>
            </Card>
            <Card className="bg-primary text-primary-foreground">
              <CardContent className="p-5">
                <div className="text-xs text-primary-foreground/80">Return on investment</div>
                <div className="text-3xl font-bold mt-1">{result.roi.toFixed(0)}%</div>
                <div className="text-xs text-primary-foreground/80 mt-1">First-year impact</div>
              </CardContent>
            </Card>
          </div>

          <Card className="print-card">
            <CardHeader>
              <CardTitle className="text-base">Where the saving comes from</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Productivity recovered (faster ramp)</span>
                <span className="font-mono font-semibold">{fmt(result.recoveredProductivity)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Revenue retained (relationship handover)</span>
                <span className="font-mono font-semibold">{fmt(result.recoveredRevenue)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-base">
                <span className="font-semibold">Total annual recovery</span>
                <span className="font-mono font-bold">{fmt(result.totalSavings)}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500 print-card">
            <CardContent className="p-5 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Conservative by design.</p>
                <p className="text-muted-foreground mt-1">
                  This model assumes only 15% of revenue actually churns without a structured handover, and only 70% of
                  that is recoverable with one. Real outcomes are typically higher. We will gladly walk your CFO through
                  every assumption.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="print-card">
            <CardHeader>
              <CardTitle className="text-base">Sources & defaults</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside leading-relaxed">
                {FOOTNOTES.map(([title, body]) => (
                  <li key={title}>
                    <span className="font-semibold text-foreground">{title}.</span> {body}
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>

          <div className="no-print text-center pt-2">
            <Button onClick={() => setLocation("/dashboard")} className="gap-2">
              <PoundSterling className="h-4 w-4" /> Start a transition with these numbers
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
